import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, Pages } from "../../../../Navigation/root";
import { colors, fonts, frames, glyphs, useMediaQuery } from "../../../../Styles";
import { PageHeader, ProgressBar, QuickEntryModal, AddAnimalModal, NewInput, FilterPopup, FilterDisplayList, Overlay } from "../../../../Components";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, SelectFieldOption_Values, TableStyles_Types, NewInputHandles, ValueSteps } from "../../../../Definitions";
import { UndefinedOptions, useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions, useSystemOptions } from "../../../../Components/_Components";
import { Graph_QL, useAnimal, MaintainAnimal, useFeedCurves } from "../../../../Services";
import { AutoReport } from "../../../../Services/Agrisys_GqlDataCaller/WidgetCalls/GQLCalls/autoReportCall";
import { useAppSelector } from "../../../../Store";
import { cloneDeep, keyBy, merge } from "lodash";

import { exportTableToCsv } from "../Reports/_Reports/exportTableToCsv";
import { exportTableToExcel } from "../Reports/_Reports/exportTableToXml";
import { ProgressTypes } from "../../../../Components/ProgressViewer/progressBar";

// Table
import { ColumnFiltersState, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, noop, Row, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../../../Components/Tables/reactiveTableRenderer";
import { GenericDataParameters, GenericRow, Units, useColumnConstructor } from "../../../../Components/Tables/_Tables/useColumnConstructor";
import globalFilter from "../../../../Components/Tables/_Tables/useGlobalFilter";
import { LOCKED_COLUMN_NAMES } from "../../../../Components/Tables/_Tables/getTableContent";
import NewButton, { NewButtonList } from "../../../../Components/BaseComponents/newButton";
import FeedLabel from "../../../../Components/ProgressViewer/SubComponents/feedLabel";
// import { Units } from "../../../../Styles/units";

// const createConditionTableDivStyleFunction = (conditionData: Condition[]) => {
//   const colorTagFromName = (name: string|number) => {return conditionData.find(condition => condition.conditionName === name)?.conditionColorTag}
//   return (name?: string | number):CSSProperties|undefined => { if (!name) {return undefined} return {backgroundColor: colorTagFromName(name), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}}
// }

function OverFed({header}:{header: string}) {
  return (
    <div style={{ width: "100%", display: "flex", alignItems: "center", gap: "5px", overflow: "hidden" }}>
      <div style={{ width: "100px", flexShrink: "0" }}>{header}</div>
      <FeedLabel/>
    </div>
  )
}

interface AnimalTableData extends GenericRow {
  index: number;
  feedCurveId?: SelectFieldOption_Values;
}
// 
export default function Animals() {
  const navigator = useNavigate();
  const { t } = useTranslation();
  let location = useLocation();
  const [editTable, setEditTable] = useState(false);
  const [quickEntryModal, setQuickEntryModal] = useState(false);
  const [addAnimalModal, setAddAnimalModal] = useState(false);
  const searchRef = useRef<NewInputHandles>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  
  const mobileXs = useMediaQuery(`(max-width: 640px)`);
  const hideFilterDisplay = useMediaQuery(`(max-width: 765px), (min-width: 1001px) and (max-width: 1100px)`);
  // const divStyleOverride = useMemo(() => (createConditionTableDivStyleFunction(conditionData)),[]); // TODO
  const dirtRef = useRef<MaintainAnimal[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  
  const { feedcurveOptions } = useFeedcurveOptions(UndefinedOptions.undefined);
  const { locationOptions } = useSectionOptions(UndefinedOptions.undefined);
  const groupOptions = useGroupOptions(UndefinedOptions.undefined);
  const milestoneOptions = useMilestoneOptions();
  const systemOptions = useSystemOptions();
  
  const [autoReport, setAutoReport] = useState<AutoReport>();

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { call: getAnimalsInfo, data: animalsState, maintainEntries: updateAnimals } = useAnimal(connection);
  const { call: getFeedCurves, data: feedCurvesState } = useFeedCurves(connection);

  const [animals, setAnimals] = useState(animalsState && animalsState[domain] ? cloneDeep(animalsState[domain].data) : []);

  const feedCurves = useMemo(() => {
    if (feedCurvesState && feedCurvesState[domain]) {
      return feedCurvesState[domain].data;
    }
    return [];
  }, [domain, feedCurvesState]);

  useEffect(() => {
    getFeedCurves({ fetchAmount: 50, ifFeedCurveBaseInfoOnly: true });
    getAnimalsInfo({ fetchAmount: 51 }, connection);
  }, [connection]);

  useEffect(() => {
    setAnimals(
      animalsState && animalsState[domain]
        ? cloneDeep(animalsState[domain].data)
        : []
    );
  }, [animalsState, domain]);

  useEffect(() => {
    const selected = location.state?.params?.selected;
    if (selected) {
      setAutoReport(selected);
    }
  }, [location.state]);

  useEffect(() => {
    setGlobalFilterValue(searchTerm);
  }, [searchTerm]);

  // #region | Animals Table
  const tableData: AnimalTableData[] = useMemo(() => animals.map((animal, index) => {
    const consumptionToday = animal.consumptions ? animal.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 0):undefined;
    const consumptionTotalToday = +((consumptionToday?.total ?? 0) / 1000).toFixed(2);
    const consumptionProgressedToday = +(consumptionTotalToday - (consumptionToday?.progressed ?? 0) / 1000).toFixed(2);
    const progressBarToday = ( <ProgressBar total={consumptionTotalToday} progressed={consumptionProgressedToday} extension={{hasExtension: true}}/> );
    const animalConsumedToday = <ProgressBar progressType={ProgressTypes.consumed} total={consumptionTotalToday} progressed={+((consumptionToday?.progressed ?? 0) / 1000).toFixed(2)} extension={{hasExtension: true}} />
    const consumptionYesterday = animal.consumptions ? animal.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 1):undefined;
    const consumptionTotalYesterday = +((consumptionYesterday?.total ?? 0) / 1000).toFixed(2);
    const consumptionProgressedYesterday = +(consumptionTotalYesterday - (consumptionYesterday?.progressed ?? 0) / 1000).toFixed(2);
    const progressBarYesterday = ( <ProgressBar total={consumptionTotalYesterday} progressed={consumptionProgressedYesterday} extension={{hasExtension: true}}/> );
    const animalConsumedYesterday = <ProgressBar progressType={ProgressTypes.consumed} total={consumptionTotalYesterday} progressed={+((consumptionYesterday?.progressed ?? 0) / 1000).toFixed(2)} extension={{hasExtension: true}} />
    
    const actionButton = ( <NewButton
      content={animal.animal_number.toString()}
      buttonType={ButtonTypes.textButton}
      startEnhancer={ <glyphs.ArrowSmooth fillColor={colors.greyDarkSecondary} /> }
      onClick={() => { RootNavigation({ Navigator: navigator, navigation_page: Pages.animals, navigation_tab: Pages.animal, replace: true, navArgs: { selected: animal.animal_id }, currentDomain: farm }); }}
      overrides={{
        contentStyles: { color: colors.greyDarkPrimary, ...fonts.fontSize4, ...fonts.paragraph },
        startEnhancerStyles: { marginRight: "15px" },
      }}
    /> );

    return {
      animal_number: editTable ? animal.animal_number : actionButton,
      id: animal.animal_id,
      // animalType: animal.animalType,
      life_number: animal.life_number,
      responderNumber: animal.responder_number ? animal.responder_number : "",
      // animalCondition: animal.condition? animal.condition : 0,
      system_id: animal.system ?  systemOptions?.find(({ label }) => animal.system === label) : undefined,
      animal_group_id: { label: animal.animalGroup.name ?? "", id: animal.animalGroup.groupId },
      location_id: animal.location_id ? locationOptions?.find(({ id }) => animal.location_id === id) : undefined,
      [LOCKED_COLUMN_NAMES.feedCurveId]: {label: animal.feedCurve?.name ?? "", id: animal.feedCurve?.[LOCKED_COLUMN_NAMES.feedCurveId]},
      animalRemainingToday: progressBarToday,
      animalRemainingYesterday: progressBarYesterday,
      animalConsumedToday,
      animalConsumedYesterday,
      [LOCKED_COLUMN_NAMES.milestone_id]: { id: animal.milestone?.[LOCKED_COLUMN_NAMES.milestone_id], label: animal.milestone?.name ?? "" },
      animalMilestoneDay: animal.milestone_day ? Math.floor((new Date().getTime() - animal.milestone_day) / (1000 * 60 * 60 * 24)) : t("noData", { defaultValue: "no data" }),
      animalCycleDayTotal: animal.birthday ? Math.floor((new Date().getTime() - animal.birthday) / (1000 * 60 * 60 *24)) : t("noData", {defaultValue: "no data"}), //animal.cycle,
      index,
    };
  }), [animals, editTable, systemOptions, locationOptions, t, navigator]);

  const accessorData = useMemo<GenericDataParameters<AnimalTableData>[]>( () => [
    { headerValue: t("params.animalId", { defaultValue: "animal no." }), accessor: "animal_number", type: editTable ? ListData_ValueTypes.Number : ListData_ValueTypes.Button, width: "130px", staticColumn: true, filter: true },
    // {headerValue: t("params.animalType", {defaultValue: "animal type"}), accessor: "animalType", type: ListData_ValueTypes.String, sort: true, width: "160px"},
    { headerValue: t("params.lifeNo", { defaultValue: "life no." }), accessor: "life_number", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers, sort: true, width: "160px" },
    { headerValue: t("params.responderNo", { defaultValue: "responder no." }), accessor: "responderNumber", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers, sort: true,  width: "200px" },
    // {headerValue: t("params.condition", {defaultValue: "condition"}), accessor: "animalCondition", type: ListData_ValueTypes.List, options: conditionOptions, sort: true, width: "160px", divStyleOverride},
    { headerValue: t("params.group", { defaultValue: "group" }), accessor: "animal_group_id", type: ListData_ValueTypes.List, options: groupOptions, sort: true, width: "160px", filter: true },
    { headerValue: t("params.location", { defaultValue: "location" }), accessor: "location_id", type: ListData_ValueTypes.List,  options: locationOptions, sort: true, width: "200px" },
    { headerValue: t("feed.feedCurve.feedCurve", { defaultValue: "feed curve", }), accessor: LOCKED_COLUMN_NAMES.feedCurveId, type: ListData_ValueTypes.List, options: feedcurveOptions, sort: true, width: "200px", filter: true },
    { headerValue: ( <OverFed header={t("feed.general.remainingToday", { defaultValue: "remaining today" })} /> ), accessor: "animalRemainingToday", type: ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "200px", unit: Units.kg},
    { headerValue: ( <OverFed header={t("feed.general.remainingYesterday", { defaultValue: "remaining yesterday" })} /> ), accessor: "animalRemainingYesterday", type: ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "200px", unit: Units.kg },
    { headerValue: ( <OverFed header={t("feed.general.consumedToday", { defaultValue: "consumed today" })} /> ), accessor: "animalConsumedToday", type: ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "200px", unit: Units.kg},
    { headerValue: ( <OverFed header={t("feed.general.consumedYesterday", { defaultValue: "consumed yesterday" })} />), accessor: "animalConsumedYesterday", type: ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "200px", unit: Units.kg},
    { headerValue: t("milestones.general.latestMilestone", { defaultValue: "curr. milestone", }), accessor: LOCKED_COLUMN_NAMES.milestone_id, type: ListData_ValueTypes.List,
      options: (row?: Row<AnimalTableData>) => {
        if (!row) {
          return milestoneOptions;
        }
        return (
          feedCurves
            .find(
              (feedCurve) =>
                row.original[LOCKED_COLUMN_NAMES.feedCurveId]?.id === feedCurve[LOCKED_COLUMN_NAMES.feedCurveId]
            )
            ?.milestones?.map((milestone) => ({
              id: milestone.feedCurveMilestoneId,
              label: milestone.name,
            })) ?? []
        );
      },
    sort: true, width: "200px", filter: true },
    { headerValue: t("milestones.general.milestoneDayNow", { defaultValue: "curr. milestone day", }), accessor: "animalMilestoneDay", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers, sort: true, width: "230px" },
    { headerValue: t("animalPages.animal.totalCycleDay", { efaultValue: "total cycle day", }),  accessor: "animalCycleDayTotal", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers, sort: true,  width: "200px" },
    { headerValue: t("animalPages.animal.systemName", { defaultValue: "system", }), accessor: "system_id", type: ListData_ValueTypes.List, options: systemOptions, sort: true, width: "200px", staticColumn: true, filter: true },
  ], [editTable, feedCurves, feedcurveOptions, groupOptions, locationOptions, milestoneOptions, systemOptions, t]);

  const columns = useColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: editTable,
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    autoResetPageIndex: false,
    globalFilterFn: globalFilter,
    state: {
      globalFilter: globalFilterValue,
      columnFilters,
      columnPinning: {
        left: ["animal_number", "id"],
      },
    },
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
    meta: {
      deleteRow: noop,
      // TODO REDUCE BOILER PLATE AND KEEP IT DRY
      updateData: (rowId, columnId, value, row) => {
        if (columnId === "responderNumber") {
          dirtRef.current = Object.values(
            merge(
              {},
              keyBy(dirtRef.current, "animal_id"),
              keyBy(
                [{ animal_id: rowId, responder_number: Number(value) }],
                "animal_id"
              )
            )
          );
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.responder_number = Number(value);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === LOCKED_COLUMN_NAMES.feedCurveId) {
          const selectValue = value as any as SelectFieldOption_Values;
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];

          if (newAnimal.feedCurve) {
            newAnimal.feedCurve[LOCKED_COLUMN_NAMES.feedCurveId] = Number(selectValue.id);
            newAnimal.feedCurve.name = selectValue.label;
          } else {
            newAnimal.feedCurve = {
              name: selectValue.label,
              feedCurveId: Number(selectValue.id),
              feedLimit: 0,
              passFeed: 0,
              stimuli: 0,
            };
          }

          const selectedFeedCurve = feedCurves.find(
            (feedCurve) => feedCurve.feedCurveId === Number(selectValue.id)
          );
          const currentMilestoneInNewFc = selectedFeedCurve?.milestones?.find((milestone) => milestone.milestoneId === newAnimal.milestone?.milestone_id);
          if (selectedFeedCurve?.milestones?.length) {
            if (!currentMilestoneInNewFc) {
              newAnimal.milestone = {
                name: selectedFeedCurve.milestones[0].name,
               milestone_id: selectedFeedCurve.milestones[0].milestoneId,
              };
              newAnimal.milestone_day = Date.now();
            }
          } else {
            newAnimal.milestone = undefined;
          }

          dirtRef.current = Object.values( merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{
              animal_id: rowId,
              feedcurve_id: selectValue.id,
              fk_feedcurve_milestones: selectedFeedCurve?.milestones
                ?.length && !currentMilestoneInNewFc 
                ? selectedFeedCurve?.milestones[0].feedCurveMilestoneId
                : undefined,
            }], "animal_id")
          ));
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animalCycleDayTotal") {
          const newValue = Date.now() - Number(value) * 86400000
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, birthday: Math.round(newValue / 1000) }], "animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.birthday = newValue;
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "life_number") {
          dirtRef.current = Object.values( merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, [columnId]: Number(value) }],"animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.life_number = Number(value);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animal_group_id") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtRef.current = Object.values( merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, animal_group_id: selectValue.id }], "animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          if (newAnimal.animalGroup) {
            newAnimal.animalGroup.groupId = Number(selectValue.id);
            newAnimal.animalGroup.name = selectValue.label;
          } else {
            newAnimal.animalGroup = {
              name: selectValue.label,
              groupId: Number(selectValue.id),
            };
          }
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "location_id") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, location: selectValue.id }], "animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.location_id = Number(selectValue.id);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animalMilestoneDay") {
          const newValue = Date.now() - Number(value) * 86400000;
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{animal_id: rowId, milestone_day: Math.round(newValue / 1000),}],"animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.milestone_day = newValue;
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === LOCKED_COLUMN_NAMES.milestone_id) {
          const selectValue = value as any as SelectFieldOption_Values;
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex]; // selected value might not be correct
          if (row.original.feedCurveId?.id) {
            const feedCurveId = feedCurves.find(
              (feedCurve) =>
                feedCurve[LOCKED_COLUMN_NAMES.feedCurveId] === row.original[LOCKED_COLUMN_NAMES.feedCurveId]?.id
            );
            const milestone = feedCurveId?.milestones?.find(
              (milestone) =>
                milestone.feedCurveMilestoneId === Number(selectValue.id)
            );
            if (milestone) {
              newAnimal.milestone = {
                milestone_id: milestone.milestoneId,
                name: milestone.name,
              };
              newAnimal.milestone_day = Date.now();
              dirtRef.current = Object.values( merge({},
                keyBy(dirtRef.current, "animal_id"),
                keyBy([{ animal_id: rowId, fk_feedcurve_milestones: milestone.feedCurveMilestoneId }], "animal_id")
              ));
            }}
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
      },
    },
  });

  const reactiveTable = ReactiveTable({
    minRows: mobileXs ? 26 : 24,
    table,
    tableType: TableStyles_Types.primaryTable,
    emptyText: t("animalPages.animals.emptyText", {
      defaultValue: "No Animal Found",
    }),
    // renderFooter: false,
    tableToggled: editTable,
  });
  // #endregion

  // #region | Download
  // #region | Download CSV
  const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
  const fileLabel = t("animalPages.animals.animals", {defaultValue: "animals" });

  return (
    <frames.Page_inner>
      <PageHeader
        topHeader={t(`menuHeaders.${Pages.animals}`, { defaultValue: "animals" })}
        subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
        optionButtons={{
          contents: [ t("animalPages.animals.quickEntry", { defaultValue: "Quick Entry" }) ],
          onClick: () => setQuickEntryModal(true)
        }}
      />
      <frames._page_inner_content>
        <div style={{ height: "100%", width: "100%", display: "grid", gridTemplateRows: mobileXs ? "85px 1fr 40px" : "50px 1fr 40px", boxSizing: "border-box", position: "relative", zIndex: editTable ? "999999" : "0" }}>
          {editTable ? <div style={{gridArea: "3/1/3/1", position: "relative"}}>
          </div> : <></>}
          <div style={{gridArea: "1/1/2/2", boxSizing: "border-box", width: "100%", padding: "0px 20px", display: "grid", gridTemplateColumns: mobileXs ? "1fr" : editTable ? "280px 1fr 390px" : "280px 1fr 272px", gridTemplateRows: mobileXs ? "35px 50px" : "50px"}}>
            <div style={{ marginBottom: "5px", gridArea: "1/1/2/2", display: "flex", alignItems: "end" }}>
              <NewInput ref={searchRef}
                startEnhancer={glyphs.Search({})}
                onChange={(value: string) => {setSearchTerm(value)}}
                fieldOverrides={{fieldStyle: NewFieldStyles.greySecondary}}
                placeholder={t("search", { defaultValue: "search" })}
                isDisabled={editTable}
              />
            </div>
            {hideFilterDisplay ? <></>
            : <div style={{ gridArea: "1/2/2/3", display: "flex", alignItems: "end", position: "relative" }}>
                <FilterDisplayList filters={columnFilters} disabled={editTable} setColumnFilters={setColumnFilters} />
              </div> }
            <div style={{ display: "flex", justifyContent: "end", gap: "5px", gridArea: mobileXs ? "2/1/3/2" : "1/3/2/4"}}>
              <FilterPopup<AnimalTableData> disabled={editTable} accessorData={accessorData} table={table} autoReport={autoReport ? { ...autoReport, auto_report_id: autoReport.auto_report_id ?? "", filter: autoReport.filter ?? [] } : undefined} />
              {!editTable ? (
                <NewButtonList
                  contents={[
                    t("buttons.add", { defaultValue: "add" }),
                    t("buttons.edit", { defaultValue: "edit" }),
                    t("buttons.excel", { defaultValue: "excel" }),
                    t("buttons.csv", { defaultValue: "csv" }),
                  ]}
                  onClick={(i) => {
                    if (i === 0) { setAddAnimalModal(true) }
                    if (i === 1) { setEditTable(true) }
                    if (i === 2) { exportTableToExcel(filteredData, `${fileLabel}.xlsx`, fileLabel, accessorData) }
                    if (i === 3) { exportTableToCsv(filteredData, `${fileLabel}.csv`, accessorData);}
                  }}
                  buttonType={ButtonTypes.tableButton}
                  startEnhancers={[ glyphs.Add({}), glyphs.Edit({}), glyphs.Excel({}), glyphs.Csv({}) ]}
                />
              ) : (
                <NewButtonList
                  contents={[
                    t("buttons.save", { defaultValue: "save" }),
                    t("buttons.cancel", { defaultValue: "cancel" }),
                    t("buttons.add", { defaultValue: "add" }),
                    t("buttons.edit", { defaultValue: "edit" }),
                    t("buttons.excel", { defaultValue: "excel" }),
                    t("buttons.csv", { defaultValue: "csv" }),
                  ]}
                  onClick={(i) => {
                    if (i === 0) {
                      if (updateAnimals) {
                        updateAnimals({ data: dirtRef.current })
                      }
                      dirtRef.current = []; setEditTable(false)
                    }
                    if (i === 1) { setAnimals(animalsState && animalsState[domain] ? cloneDeep(animalsState[domain].data) : []); dirtRef.current = []; setEditTable(false);}
                    if (i === 2) {}
                    if (i === 3) {}
                    if (i === 4) {}
                    if (i === 5) {}
                  }}
                  buttonType={ButtonTypes.tableButton}
                  startEnhancers={[ glyphs.Check({}), glyphs.Close({}), glyphs.Add({}), glyphs.Edit({}), glyphs.Close({}), glyphs.Close({}) ]}
                  disabled={[false, false, true, true, true, true]}
                  overrides={(index) => ({
                    baseStyles: { backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary },
                    disabledStyles: { opacity: index === 2 || index > 3 ? 0.5 : 1 },
                  })}
                />
              )}
            </div>
          </div>
          <div style={{ gridArea: "2/1/3/2", height: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", overflow: "hidden" }}>
            {animals.length === 0 ? <p style={{...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary, textAlign: "center", padding: "20px"}}>{t("animalPages.animals.emptyText", {defaultValue: "No animals found"})}</p> : <>{reactiveTable}</>}
          </div>
          <div style={{ gridArea: "3/1/4/2", display: "flex", alignItems: "center", gap: "5px", padding: "5px 20px 0px 20px" }}>
            <NewButton
              content={""}
              buttonType={ButtonTypes.arrowButton}
              onClick={() => { if (table.getCanPreviousPage()) {getAnimalsInfo({ fetchAmount: 51, offset: 51*(table.getState().pagination.pageIndex-1)}, connection); table.previousPage()} }}
              startEnhancer={glyphs.Arrow({ rotate: 180 })}
              overrides={{
                baseStyles: { backgroundColor: colors.greyPrimary },
                hoverStyles: { backgroundColor: colors.greySecondary },
              }}
              disabled={editTable || !table.getCanPreviousPage()}
            />
            <NewButton
              content={""}
              buttonType={ButtonTypes.arrowButton}
              onClick={() => { if (table.getCanNextPage()) {getAnimalsInfo({ fetchAmount: 51, offset: 51*(table.getState().pagination.pageIndex+1)}, connection); table.nextPage()} }}
              startEnhancer={glyphs.Arrow({})}
              overrides={{
                baseStyles: { backgroundColor: colors.greyPrimary },
                hoverStyles: { backgroundColor: colors.greySecondary },
              }}
              disabled={editTable || !table.getCanNextPage()}
            />
            <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
              {`${t("page", { defaultValue: "page" })} ${table.getState().pagination.pageIndex + 1} ${t("of", { defaultValue: "of" })} ${table.getPageCount()}`}
            </p>
          </div>
        </div>
      </frames._page_inner_content>

      <AddAnimalModal
        isModalOpen={addAnimalModal}
        closeModal={() => setAddAnimalModal(false)}
        connect={{
          connection: connection,
          domain: domain
        }}
      />
      <QuickEntryModal
        isModalOpen={quickEntryModal}
        closeModal={() => setQuickEntryModal(false)}
      />

      {editTable ? <Overlay overrides={{ zIndex: editTable ? 99999 : 0 }} beforeUnload onClickAlert /> : <></>}
    </frames.Page_inner>
  );
}
